import { Container, Graphics } from 'pixi.js';
import { place, type RoomContext, type RoomDecorator } from './index';
import { registerScenery, synthCtx } from '../scenery';

/**
 * LOUNGE — the office's cosy break room (Floorplan v2 rect 40,280 · 400×300; interior 40..440 × 280..580,
 * centre 240,430). Redesigned for ORDER + STYLE: one tight, inviting conversation nook as the hero, tidy
 * edges, and EVERY chair genuinely sittable — no clutter, no stray rug-blobs, no decoy seats.
 *
 * ── THE SEAT CONTRACT (authoritative; this file only DRAWS to match it) ──────────────────────────────
 * Seats live in `packages/contracts/src/world/seats.ts` — frozen here, not editable from a room module.
 * The lounge has exactly TWO real, sittable anchors, and every chair-shaped object below lands ON one of
 * them so the player can never walk up to a chair they cannot use (UX: no broken affordance):
 *   - lounge-sofa     → (150,400) facing 0    (east, into the room)
 *   - lounge-armchair → (330,360) facing π    (west, toward the sofa)
 * The previous version drew the armchair body at y=400 (off its y=360 anchor); this redesign puts it
 * dead on (330,360) so the visual seat and the sittable seat are the same spot.
 *
 *   ┌──────────────────────────────── interior 40..440 × 280..580 ─────────────────────────────────┐
 *   │  [wall-art]        [BOOKSHELF]                                                                │
 *   │  [plant NW]                                          [armchair ◄ 330,360 faces W]             │
 *   │                          ╭───── ONE RUG ─────╮                                                │
 *   │              [SOFA 150,400 faces E ►]   ◯table    ░ E DOOR (y350..510) kept clear ░           │
 *   │                          ╰───────────────────╯                                                │
 *   │   reading vignette: lamp + side table + plant SE                                              │
 *   │                  ░░ S DOOR x160..320 kept clear ░░                       [plant SE]           │
 *   └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * THE HERO NOOK (Art Director: one clear focal point; UX: an obvious "sit here together" read).
 *   - SOFA on its anchor (150,400), rotated so its FRONT (+y local) faces RIGHT/east into the room.
 *   - ARMCHAIR on its anchor (330,360), rotated so its FRONT faces LEFT/west — a clean face-off.
 *   - ROUND COFFEE TABLE between them at ~(240,388) with two mugs + a tray, the heart of the group.
 *   - Exactly ONE neat rectangular RUG (250×150) under the whole group, inset well off every wall and
 *     clear of both door lanes — the only rug in the room, no stray blobs.
 *
 * TIDY EDGES (deliberate, never cluttered):
 *   - NORTH wall: a BOOKSHELF feature with a single framed WALL-ART beside it — a furnished back wall.
 *   - READING VIGNETTE (SW edge, below the sofa): a floor LAMP + a small side table with a book, giving
 *     the warm reading-corner feel WITHOUT a decoy chair (the only sittable chairs are the two anchors).
 *   - The signature HOOKAH tucked low-left, the lounge's relax icon, off the south door lane.
 *   - Exactly TWO PLANTS, one per back corner (NW + SE), framing the room and nothing more.
 *
 * Everything themes off `art` (re-skins per world) and is built ONCE on map build — no animation, no
 * per-frame allocation. Soft glow pools make the room read warmly lamp-lit.
 */
/**
 * The SW reading-vignette SIDE TABLE: a small round table with a closed book + a coffee cup on top, drawn
 * inline (lounge-only) so it nestles by the floor-lamp glow without a decoy chair. Origin centred on the
 * table base (the top sits just above it); the contact shadow falls "down" (+y).
 */
function sideTable(ctx: RoomContext): Container {
  const { art, lighten, darken } = ctx;
  const c = new Container();
  c.addChild(
    new Graphics()
      // contact shadow + round table top
      .ellipse(0, 10, 18, 6)
      .fill({ color: 0x000000, alpha: 0.18 })
      .ellipse(0, 0, 15, 7)
      .fill(darken(art.wall, 0.12))
      .ellipse(0, -3, 15, 7)
      .fill(art.wall)
      // a closed book on top
      .roundRect(-10, -10, 20, 8, 1.5)
      .fill(lighten(art.accent, 0.1))
      .roundRect(-10, -10, 20, 2.5, 1.5)
      .fill({ color: 0xffffff, alpha: 0.18 })
      // a tiny coffee cup beside it
      .circle(9, -6, 3)
      .fill(lighten(art.glow, 0.2)),
  );
  return c;
}

registerScenery('lounge-side-table', (art) => sideTable(synthCtx(art)), 'Side table', '🛋️');

const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { container, art, f, lighten, darken } = ctx;

  // ── Ambient warmth: two soft pools of light so the room feels lamp-lit and cosy (build-once mood
  // wash) — one centred on the conversation nook, one on the SW reading vignette. Off the door lanes. ──
  container.addChild(
    new Graphics()
      .ellipse(240, 395, 145, 90)
      .fill({ color: art.glow, alpha: 0.07 * art.glowStrength + 0.04 })
      .ellipse(110, 470, 58, 48)
      .fill({ color: art.glow, alpha: 0.06 * art.glowStrength + 0.03 }),
  );

  // ── THE CONVERSATION NOOK (hero, centred) ───────────────────────────────────────────────────────────
  // Exactly ONE neat rectangular rug under the whole group, centred ~(240,388). 250×150 → spans
  // x 115..365, y 313..463: inset ≥75px off every wall and clear of the E door band (x>365 at y350..510
  // stays open) and the S door lane (x160..320 near y580 stays open). The room's only rug — no blobs.
  place(ctx, f.rug(art, 250, 150), 240, 388);

  // SOFA on its seat anchor (150,400), rotated so its FRONT (+y local) faces RIGHT/east into the room.
  // (Built facing 'down'; rotation −π/2 maps local +y → world +x, matching the seat's facing = 0.)
  const sofa = place(ctx, f.sofa(art), 150, 400);
  sofa.rotation = -Math.PI / 2;

  // ARMCHAIR dead on its seat anchor (330,360), rotated so its FRONT faces LEFT/west toward the sofa —
  // the face-off reads tight and the visual chair == the sittable chair.
  // (Rotation +π/2 maps local +y → world −x, matching the seat's facing = π.)
  const armchair = place(ctx, f.armchair(art), 330, 360);
  armchair.rotation = Math.PI / 2;

  // ROUND COFFEE TABLE between the two seats (on the rug), the heart of the group.
  place(ctx, f.coffeeTable(art), 240, 388);

  // A couple of tiny props ON the coffee table: a little tray + two mugs with a steam wisp each.
  container.addChild(
    new Graphics()
      // tray
      .roundRect(240 - 16, 386 - 6, 32, 12, 3)
      .fill({ color: darken(art.wall, 0.1), alpha: 0.9 })
      // two mugs
      .circle(240 - 7, 386, 4)
      .fill(lighten(art.accent, 0.25))
      .circle(240 + 7, 386, 4)
      .fill(lighten(art.glow, 0.2))
      // a steam wisp hint on each
      .circle(240 - 7, 386 - 2, 1.6)
      .fill({ color: 0xffffff, alpha: 0.18 })
      .circle(240 + 7, 386 - 2, 1.6)
      .fill({ color: 0xffffff, alpha: 0.18 }),
  );

  // ── NORTH WALL: a bookshelf feature + a single framed wall-art beside it (body tops ≥16px off wall). ──
  place(ctx, f.bookshelf(art), 200, 328);
  place(ctx, f.wallArt(art, 'painting'), 300, 300);

  // ── SW READING VIGNETTE (below the sofa): a floor lamp + a small side table with a book. Reads as a
  // warm reading corner WITHOUT a decoy chair — the only sittable chairs are the two real anchors. ──────
  place(ctx, f.lamp(art), 78, 470);
  // A small bespoke side table with a closed book + a coffee cup, nestled by the lamp glow.
  place(ctx, sideTable(ctx), 130, 478, 'lounge-side-table');

  // The signature HOOKAH tucked low-left — the relax-room icon, clear of the S door lane (x<160). ──────
  place(ctx, f.hookah(art), 92, 540);

  // ── EXACTLY TWO PLANTS, one per back corner, framing the room (no scattered greenery). ───────────────
  place(ctx, f.plant(art, 'tall'), 70, 334); // NW corner (body ≥16px off both walls)
  place(ctx, f.plant(art, 'leafy'), 408, 540); // SE corner (below the E door lane, off the S door lane)
};

export default decorate;
